import { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  Settings,
} from '@mui/icons-material';
import type { TextElement as TextElementType } from '@/types/thinkspace';

const FONT_FAMILIES = [
  'Space Grotesk',
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Comic Sans MS',
];

const TEXT_COLORS = [
  { name: 'Black', value: '#151414' },
  { name: 'Dark Gray', value: '#424242' },
  { name: 'Gray', value: '#757575' },
  { name: 'Blue', value: '#1976d2' },
  { name: 'Red', value: '#d32f2f' },
  { name: 'Green', value: '#388e3c' },
  { name: 'Purple', value: '#7b1fa2' },
  { name: 'Orange', value: '#f57c00' },
];

type TextElementProps = {
  element: TextElementType;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<TextElementType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
};

export const TextElement = ({
  element,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onDragStart,
}: TextElementProps) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(element.content);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [propsContent, setPropsContent] = useState(element.content);
  const [fontSize, setFontSize] = useState(element.fontSize);
  const [fontFamily, setFontFamily] = useState(element.fontFamily);
  const [color, setColor] = useState(element.color);
  const [bold, setBold] = useState(element.bold);
  const [italic, setItalic] = useState(element.italic);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPropsContent(element.content);
    setFontSize(element.fontSize);
    setFontFamily(element.fontFamily);
    setColor(element.color);
    setBold(element.bold);
    setItalic(element.italic);
  }, [element.content, element.fontSize, element.fontFamily, element.color, element.bold, element.italic]);

  const handleSaveProperties = () => {
    onUpdate({ content: propsContent, fontSize, fontFamily, color, bold, italic });
    setPropertiesOpen(false);
  };

  const handleCancelProperties = () => {
    setPropsContent(element.content);
    setFontSize(element.fontSize);
    setFontFamily(element.fontFamily);
    setColor(element.color);
    setBold(element.bold);
    setItalic(element.italic);
    setPropertiesOpen(false);
  };

  useEffect(() => {
    setContent(element.content);
  }, [element.content]);

  const handleSave = () => {
    if (content !== element.content) {
      onUpdate({ content });
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setContent(element.content);
      setEditing(false);
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSave();
    }
  };

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={() => canEdit && setEditing(true)}
      onMouseDown={canEdit && !editing && !propertiesOpen ? onDragStart : undefined}
      onTouchStart={canEdit && !editing && !propertiesOpen ? onDragStart : undefined}
      sx={{
        position: 'absolute',
        left: element.position.x,
        top: element.position.y,
        minWidth: 50,
        padding: 0.5,
        borderRadius: 0.5,
        border: selected ? '2px solid #0f2ccf' : '2px solid transparent',
        cursor: canEdit && !editing ? 'grab' : 'text',
        userSelect: 'none',
        touchAction: 'none',
        zIndex: element.zIndex,
        backgroundColor: selected ? 'rgba(15, 44, 207, 0.05)' : 'transparent',
      }}
    >
      {editing ? (
        <TextField
          inputRef={inputRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          autoFocus
          variant="standard"
          multiline
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: element.fontSize,
              fontFamily: element.fontFamily,
              color: element.color,
              fontWeight: element.bold ? 700 : 400,
              fontStyle: element.italic ? 'italic' : 'normal',
            },
          }}
        />
      ) : (
        <Box
          sx={{
            fontSize: element.fontSize,
            fontFamily: element.fontFamily,
            color: element.color,
            fontWeight: element.bold ? 700 : 400,
            fontStyle: element.italic ? 'italic' : 'normal',
            whiteSpace: 'pre-wrap',
            minHeight: '1.2em',
          }}
        >
          {element.content || 'Text'}
        </Box>
      )}

      {canEdit && selected && !editing && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            position: 'absolute',
            top: -16,
            right: -16,
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropertiesOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog
        open={propertiesOpen}
        onClose={handleCancelProperties}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Text Properties</DialogTitle>
        <DialogContent>
          <TextField
            label="Text Content"
            value={propsContent}
            onChange={(e) => setPropsContent(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Font Size: {fontSize}px</Typography>
            <Slider
              value={fontSize}
              onChange={(_, v) => setFontSize(v as number)}
              min={8}
              max={72}
              step={1}
              valueLabelDisplay="auto"
            />
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Font Family</InputLabel>
            <Select
              value={fontFamily}
              label="Font Family"
              onChange={(e) => setFontFamily(e.target.value)}
            >
              {FONT_FAMILIES.map((font) => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                  {font}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={bold} onChange={(e) => setBold(e.target.checked)} />}
              label="Bold"
            />
            <FormControlLabel
              control={<Switch checked={italic} onChange={(e) => setItalic(e.target.checked)} />}
              label="Italic"
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Text Color</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {TEXT_COLORS.map((c) => (
                <Box
                  key={c.value}
                  onClick={() => setColor(c.value)}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: c.value,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: color === c.value ? '3px solid #0f2ccf' : '1px solid #ccc',
                    '&:hover': { opacity: 0.8 },
                  }}
                  title={c.name}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography gutterBottom variant="caption" color="text.secondary">
              Preview
            </Typography>
            <Typography
              sx={{
                fontSize,
                fontFamily,
                color,
                fontWeight: bold ? 700 : 400,
                fontStyle: italic ? 'italic' : 'normal',
              }}
            >
              {propsContent || 'Sample Text'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelProperties}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProperties}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
